import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Typography
} from '@mui/material';
import { UploadFile as UploadFileIcon } from '@mui/icons-material';
import EmployeeDocuments from '../client-employees/EmployeeDocuments';
import UploadOrgDocumentDialog from './UploadOrgDocumentDialog';
import { tcOrgDocumentApi } from './orgDocumentApi';
import tcDocumentTypeService from '../../services/tc-document-type.service';

/** A client's company documents (trade licence, establishment card, ...) with upload, view and delete. */
const OrgDocumentsTab = ({ orgId, canWrite, canDelete, api: portalApi, loadTypes }) => {
  const api = useMemo(() => portalApi || tcOrgDocumentApi(orgId), [portalApi, orgId]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [documentTypes, setDocumentTypes] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewingKey, setViewingKey] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.documents();
      setGroups(result.groups);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canWrite) return;
    (loadTypes ? loadTypes() : tcDocumentTypeService.getActive('ORGANIZATION'))
      .then((types) => setDocumentTypes(types.map((t) => ({ code: t.code, label: t.label }))))
      .catch(() => setDocumentTypes([]));
  }, [canWrite, loadTypes]);

  const handleView = async (filePath, key) => {
    setViewingKey(key);
    try {
      const resp = await api.view(filePath);
      const blob = new Blob([resp.data], { type: resp.data?.type || resp.headers?.['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError('Failed to open the document');
    } finally {
      setViewingKey(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await api.remove(deleteTarget.groupId);
      setNotice(`${deleteTarget.label} deleted permanently`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setDeleteError(e?.response?.data?.message || e?.response?.data?.error || 'Failed to delete the document');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card>
      <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Company documents ({groups.length})</Typography>
        {canWrite && (
          <Button variant="contained" size="small" startIcon={<UploadFileIcon />} onClick={() => setUploadOpen(true)}>
            Upload document
          </Button>
        )}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : groups.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>No company documents yet.</Typography>
      ) : (
        <EmployeeDocuments
          uid={orgId}
          groups={groups}
          onView={handleView}
          viewingKey={viewingKey}
          onDelete={canDelete ? setDeleteTarget : undefined}
        />
      )}
      </CardContent>

      <UploadOrgDocumentDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        api={api}
        documentTypes={documentTypes}
        onUploaded={() => { setUploadOpen(false); setNotice('Document uploaded.'); load(); }}
      />

      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '0.95rem' }}>Delete document permanently?</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 1 }}>{deleteError}</Alert>}
          <DialogContentText sx={{ fontSize: '0.85rem' }}>
            {deleteTarget?.label} ({deleteTarget?.fileCount} file{deleteTarget?.fileCount === 1 ? '' : 's'}) will be removed for good.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default OrgDocumentsTab;
